import type { PaginationMetadata } from '../models/opportunity.model';

export function unwrapApiPayload<T = unknown>(raw: unknown): T {
  if (!raw || typeof raw !== 'object') {
    return raw as T;
  }

  const envelope = raw as Record<string, unknown>;
  if (envelope['success'] !== true || !('data' in envelope)) {
    return raw as T;
  }

  const inner = envelope['data'];
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const nested = inner as Record<string, unknown>;
    if (nested['success'] === true && 'data' in nested) {
      return unwrapApiPayload<T>(inner);
    }
  }

  return inner as T;
}

export function extractPaginatedCollection(
  raw: unknown,
  preferredPath?: string,
): unknown[] {
  if (preferredPath?.trim()) {
    const preferred = getByPath(raw, preferredPath);
    if (Array.isArray(preferred)) {
      return preferred;
    }
  }

  if (raw && typeof raw === 'object') {
    const envelope = raw as Record<string, unknown>;
    if (Array.isArray(envelope['data'])) {
      return envelope['data'];
    }
  }

  const payload = unwrapApiPayload(raw);
  const paths = [
    preferredPath,
    'data',
    'data.items',
    'items',
    'data.GENDER',
    'GENDER',
  ].filter((path): path is string => !!path?.trim());

  for (const path of paths) {
    const value = getByPath(payload, path);
    if (Array.isArray(value)) {
      return value;
    }
    if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown }).items)) {
      return (value as { items: unknown[] }).items;
    }
  }

  return Array.isArray(payload) ? payload : [];
}

function defaultPaginationMetadata(collectionLength: number): PaginationMetadata {
  return {
    total: collectionLength,
    page: 1,
    limit: collectionLength,
    totalPages: 1,
    hasMore: false,
  };
}

function paginationMetadataFromPartial(
  meta: Partial<PaginationMetadata>,
  collectionLength: number,
): PaginationMetadata {
  return {
    total: meta.total ?? collectionLength,
    page: meta.page ?? 1,
    limit: meta.limit ?? collectionLength,
    totalPages: meta.totalPages ?? 1,
    hasMore: meta.hasMore ?? false,
  };
}

function paginationFromTopLevelEnvelope(
  raw: unknown,
  collectionLength: number,
): PaginationMetadata | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const envelope = raw as Record<string, unknown>;
  if (!envelope['metadata'] || typeof envelope['metadata'] !== 'object') {
    return null;
  }
  return paginationMetadataFromPartial(
    envelope['metadata'] as Partial<PaginationMetadata>,
    collectionLength,
  );
}

function paginationFromListEnvelope(
  listEnvelope: unknown,
  collectionLength: number,
): PaginationMetadata | null {
  if (
    !listEnvelope ||
    typeof listEnvelope !== 'object' ||
    Array.isArray(listEnvelope)
  ) {
    return null;
  }
  const row = listEnvelope as Record<string, unknown>;
  if (typeof row['total'] !== 'number') {
    return null;
  }
  const total = row['total'];
  const page = typeof row['page'] === 'number' ? row['page'] : 1;
  const limit =
    typeof row['limit'] === 'number' ? row['limit'] : collectionLength || 1;
  const totalPages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
  return {
    total,
    page,
    limit,
    totalPages,
    hasMore: page * limit < total,
  };
}

function paginationFromPayloadPaths(
  payload: unknown,
  collectionLength: number,
): PaginationMetadata | null {
  const metadata =
    getByPath(payload, 'metadata') ??
    getByPath(payload, 'data.metadata') ??
    getByPath(payload, 'pagination');

  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  return paginationMetadataFromPartial(
    metadata as Partial<PaginationMetadata>,
    collectionLength,
  );
}

export function extractPaginationMetadata(
  raw: unknown,
  collectionLength: number,
): PaginationMetadata {
  const fromEnvelope = paginationFromTopLevelEnvelope(raw, collectionLength);
  if (fromEnvelope) {
    return fromEnvelope;
  }

  const payload = unwrapApiPayload(raw);
  const fromList = paginationFromListEnvelope(
    getByPath(payload, 'data'),
    collectionLength,
  );
  if (fromList) {
    return fromList;
  }

  const fromPaths = paginationFromPayloadPaths(payload, collectionLength);
  if (fromPaths) {
    return fromPaths;
  }

  return defaultPaginationMetadata(collectionLength);
}

function getByPath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }

  let current: unknown = obj;
  for (const part of path
    .replace(/^\$\.?/, '')
    .split('.')
    .filter(Boolean)) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
